// ─────────────────────────────────────────────────────────
// Einstellungen → Buchhaltung & Rechnungswesen (plancraft-Stil)
// ─────────────────────────────────────────────────────────
// Zwei Bereiche in einem Modul:
//   • Nummernkreise (Prefix nr-)         → /api/accounting/number-ranges
//   • Zahlungsziel & Skonto (Prefix pt-) → /api/accounting/payment-terms
// Beide inline editierbar (kein Modal), Speichern im Kopf.
// Rechte: accounting:read / accounting:write.
//
// Sprache: sichtbare Texte über t(). Einige Texte enthalten <code>-Markup und
// werden deshalb NICHT escaped (tHtml) — zulässig, weil die Sprachdateien
// eigene, zur Bauzeit gebündelte Konstanten sind und keine Nutzereingabe.
import { icon, escapeHtml } from '../../utils/dom.js';
import { saveNumberRanges, savePaymentTerms, savePaymentCode, savePartialInvoiceSettings, saveLabourCostDisplay } from '../../core/org.js';
import { t } from '../../i18n/index.js';

// Übersetzung, die bewusst Markup enthalten darf (siehe Kopf).
const tHtml = (key) => t(key);

// ═══════════════════════════════════════════════════════════
// Nummernkreise
// ═══════════════════════════════════════════════════════════
// Schlüssel + Fallback-Präfix; Beschriftung aus settings.numberRanges.kinds.*
const DOC_KINDS = [
  ['offer', 'OF-'],
  ['invoice', 'RE-'],
];

function numberRanges(s) {
  return s.numberRanges || {};
}

// Vorschau: {prefix}{Jahr}-{0…01} (erste Nummer des Jahres)
function previewNumber(prefix, digits) {
  const year = new Date().getFullYear();
  const d = Math.min(10, Math.max(1, parseInt(digits, 10) || 3));
  const seq = String(1).padStart(d, '0');
  return `${prefix || ''}${year}-${seq}`;
}

export function numberRangesHeadButton(ctx) {
  if (!ctx.can('accounting:write')) return '';
  const saving = ctx.s.nrSaving;
  return `<button class="cd-btn-primary" data-action="nr-save" ${saving ? 'disabled' : ''}>${escapeHtml(saving ? t('common.saving') : t('common.save'))}</button>`;
}

export function renderNumberRangesSection(ctx) {
  const { s, can } = ctx;
  if (!can('accounting:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  if (s.acLoading) return `<div class="cd-set-empty">${escapeHtml(t('common.loading'))}</div>`;

  const v = numberRanges(s);
  const dis = s.nrSaving || !can('accounting:write') ? 'disabled' : '';
  const rows = DOC_KINDS.map(([k, fallbackPrefix]) => {
    const cfg = v[k] || {};
    const prefix = cfg.prefix != null ? cfg.prefix : fallbackPrefix;
    const digits = cfg.digits != null ? cfg.digits : 3;
    return `
      <div class="cd-nr-row">
        <span class="cd-nr-label">${escapeHtml(t('settings.numberRanges.kinds.' + k))}</span>
        <div class="cd-nr-fields">
          <label class="cd-nr-field">
            <span class="cd-nr-cap">${escapeHtml(t('settings.numberRanges.prefix'))}</span>
            <input class="cd-input cd-nr-in" id="nr-${k}-prefix" type="text" maxlength="12" value="${escapeHtml(String(prefix))}" placeholder="${escapeHtml(fallbackPrefix)}" ${dis}>
          </label>
          <label class="cd-nr-field">
            <span class="cd-nr-cap">${escapeHtml(t('settings.numberRanges.digits'))}</span>
            <input class="cd-input cd-nr-in cd-nr-digits" id="nr-${k}-digits" type="number" min="1" max="10" step="1" value="${Number(digits) || 3}" ${dis}>
          </label>
          <div class="cd-nr-preview">
            <span class="cd-nr-cap">${escapeHtml(t('settings.numberRanges.example'))}</span>
            <code id="nr-${k}-preview">${escapeHtml(previewNumber(prefix, digits))}</code>
          </div>
        </div>
      </div>`;
  }).join('');

  return `
    <div class="cd-emp">
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('tag', 18)}<h3>${escapeHtml(t('settings.numberRanges.title'))}</h3></div>
        <p class="cd-sc-lead">${tHtml('settings.numberRanges.lead')}</p>
        <div class="cd-nr-rows">${rows}</div>
      </div>
      <div class="cd-emp-info cd-sc-note">${icon('info', 18)}<span>${escapeHtml(t('settings.numberRanges.note'))}</span></div>
    </div>`;
}

// ═══════════════════════════════════════════════════════════
// Zahlungsziel & Skonto
// ═══════════════════════════════════════════════════════════
function paymentTerms(s) {
  return s.paymentTerms || {};
}

export function paymentTermsHeadButton(ctx) {
  if (!ctx.can('accounting:write')) return '';
  const saving = ctx.s.ptSaving;
  return `<button class="cd-btn-primary" data-action="pt-save" ${saving ? 'disabled' : ''}>${escapeHtml(saving ? t('common.saving') : t('common.save'))}</button>`;
}

export function renderPaymentTermsSection(ctx) {
  const { s, can } = ctx;
  if (!can('accounting:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  if (s.acLoading) return `<div class="cd-set-empty">${escapeHtml(t('common.loading'))}</div>`;

  const v = paymentTerms(s);
  const dis = s.ptSaving || !can('accounting:write') ? 'disabled' : '';
  const paymentDays = v.paymentDays != null ? v.paymentDays : 30;
  const skontoPercent = v.skontoPercent != null ? v.skontoPercent : 0;
  const skontoDays = v.skontoDays != null ? v.skontoDays : 0;

  return `
    <div class="cd-emp">
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('payments', 18)}<h3>${escapeHtml(t('settings.paymentTerms.title'))}</h3></div>
        <p class="cd-sc-lead">${escapeHtml(t('settings.paymentTerms.lead'))}</p>
        <div class="cd-pt-row">
          <span class="cd-pt-label">${escapeHtml(t('settings.paymentTerms.paymentDays'))}</span>
          <div class="cd-pt-inwrap">
            <input class="cd-input cd-pt-in" id="pt-paymentDays" type="number" min="0" max="365" step="1" value="${Number(paymentDays) || 0}" ${dis}>
            <span class="cd-pt-unit">${escapeHtml(t('settings.paymentTerms.days'))}</span>
          </div>
        </div>
      </div>

      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('percent', 18)}<h3>${escapeHtml(t('settings.paymentTerms.skontoTitle'))}</h3></div>
        <p class="cd-sc-lead">${escapeHtml(t('settings.paymentTerms.skontoLead'))}</p>
        <div class="cd-pt-row">
          <span class="cd-pt-label">${escapeHtml(t('settings.paymentTerms.skonto'))}</span>
          <div class="cd-pt-inwrap">
            <input class="cd-input cd-pt-in" id="pt-skontoPercent" type="number" min="0" max="100" step="0.1" value="${Number(skontoPercent) || 0}" ${dis}>
            <span class="cd-pt-unit">%</span>
          </div>
        </div>
        <div class="cd-pt-row">
          <span class="cd-pt-label">${escapeHtml(t('settings.paymentTerms.skontoWithin'))}</span>
          <div class="cd-pt-inwrap">
            <input class="cd-input cd-pt-in" id="pt-skontoDays" type="number" min="0" max="365" step="1" value="${Number(skontoDays) || 0}" ${dis}>
            <span class="cd-pt-unit">${escapeHtml(t('settings.paymentTerms.days'))}</span>
          </div>
        </div>
      </div>
      <div class="cd-emp-info cd-sc-note">${icon('info', 18)}<span>${escapeHtml(t('settings.paymentTerms.note'))}</span></div>
    </div>`;
}

// ═══════════════════════════════════════════════════════════
// Aktions-Handler (beide Bereiche)
// ═══════════════════════════════════════════════════════════
export function handleAccountingAction(a, el, ctx) {
  const { s, draw, reload } = ctx;

  if (a === 'bc-save') {
    if (!ctx.can('accounting:write')) return true;
    const payload = {
      enabled: !!document.getElementById('bc-enabled')?.checked,
      message: (document.getElementById('bc-message')?.value || '').slice(0, 140),
    };
    s.bcSaving = true; draw();
    savePaymentCode(payload)
      .then(() => { s.bcSaving = false; if (reload) reload(); else draw(); })
      .catch((err) => { s.bcSaving = false; s.error = err.message || t('common.saveFailed'); draw(); });
    return true;
  }

  if (a === 'nr-save') {
    if (!ctx.can('accounting:write')) return true;
    const readPrefix = (k) => (document.getElementById(`nr-${k}-prefix`)?.value || '').trim().slice(0, 12);
    const readDigits = (k) => Math.min(10, Math.max(1, parseInt(document.getElementById(`nr-${k}-digits`)?.value, 10) || 3));
    const payload = {};
    DOC_KINDS.forEach(([k]) => { payload[k] = { prefix: readPrefix(k), digits: readDigits(k) }; });
    s.nrSaving = true; draw();
    saveNumberRanges(payload)
      .then(() => { s.nrSaving = false; if (reload) reload(); else draw(); })
      .catch((err) => { s.nrSaving = false; s.error = err.message || t('common.saveFailed'); draw(); });
    return true;
  }

  if (a === 'lc-save') {
    if (!ctx.can('accounting:write')) return true;
    const read = (id) => !!document.getElementById(id)?.checked;
    const payload = {
      invoices: { persons: read('lc-inv-persons'), organizations: read('lc-inv-orgs') },
      offers: { persons: read('lc-off-persons'), organizations: read('lc-off-orgs') },
    };
    s.lcSaving = true; draw();
    saveLabourCostDisplay(payload)
      .then(() => { s.lcSaving = false; if (reload) reload(); else draw(); })
      .catch((err) => { s.lcSaving = false; s.error = err.message || t('common.saveFailed'); draw(); });
    return true;
  }

  if (a === 'pi-save') {
    if (!ctx.can('accounting:write')) return true;
    const payload = { cumulativeDefault: !!document.getElementById('pi-cumulativeDefault')?.checked };
    s.piSaving = true; draw();
    savePartialInvoiceSettings(payload)
      .then(() => { s.piSaving = false; if (reload) reload(); else draw(); })
      .catch((err) => { s.piSaving = false; s.error = err.message || t('common.saveFailed'); draw(); });
    return true;
  }

  if (a === 'pt-save') {
    if (!ctx.can('accounting:write')) return true;
    const num = (id, min, max) => Math.min(max, Math.max(min, parseFloat(document.getElementById(id)?.value) || 0));
    const payload = {
      paymentDays: Math.trunc(num('pt-paymentDays', 0, 365)),
      skontoPercent: num('pt-skontoPercent', 0, 100),
      skontoDays: Math.trunc(num('pt-skontoDays', 0, 365)),
    };
    s.ptSaving = true; draw();
    savePaymentTerms(payload)
      .then(() => { s.ptSaving = false; if (reload) reload(); else draw(); })
      .catch((err) => { s.ptSaving = false; s.error = err.message || t('common.saveFailed'); draw(); });
    return true;
  }

  return false;
}

// ── Bezahlcode (Swiss QR-Bill) ────────────────────────────
// plancraft hat genau einen Schalter; bei uns ist es der Zahlteil der
// Swiss QR-Bill, deshalb Default AN. Die Mitteilung trägt die Rechnungsnummer
// in die Banking-App des Kunden — damit ein Zahlungseingang zuordenbar ist.
function paymentCode(s) {
  return s.paymentCode || { enabled: true, message: 'Rechnung {number}' };
}

export function paymentCodeHeadButton(ctx) {
  if (!ctx.can('accounting:write')) return '';
  return `<button class="cd-btn-primary" data-action="bc-save" ${ctx.s.bcSaving ? 'disabled' : ''}>${escapeHtml(ctx.s.bcSaving ? t('common.saving') : t('common.save'))}</button>`;
}

export function renderPaymentCodeSection(ctx) {
  const { s, can } = ctx;
  if (!can('accounting:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  const v = paymentCode(s);
  const dis = s.bcSaving || !can('accounting:write') ? 'disabled' : '';

  return `
    <div class="cd-emp">
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('qr_code_2', 18)}<h3>${escapeHtml(t('settings.paymentCode.title'))}</h3></div>
        <label class="cd-lh-check">
          <input type="checkbox" id="bc-enabled" ${v.enabled ? 'checked' : ''} ${dis}>
          ${escapeHtml(t('settings.paymentCode.enable'))}
        </label>
        <div class="cd-dt-field">
          <label class="cd-dt-cap" for="bc-message">${escapeHtml(t('settings.paymentCode.message'))}</label>
          <input class="cd-input" id="bc-message" type="text" maxlength="140" value="${escapeHtml(v.message || '')}" placeholder="Rechnung {number}" ${dis}>
          <div class="cd-lh-hint">${tHtml('settings.paymentCode.hint')}</div>
        </div>
      </div>
      <div class="cd-emp-info cd-sc-note">${icon('info', 18)}<span>${escapeHtml(t('settings.paymentCode.note'))}</span></div>
    </div>`;
}

// ── Lohnkostenanteil ausweisen ────────────────────────────
// CH: Privatkunden können den Lohnanteil einer Handwerkerrechnung steuerlich
// geltend machen. Vier Schalter (Rechnungen/Angebote × Personen/Organisationen);
// die Zahlen selbst kommen aus der Preiskalkulation der Positionen.
function labourCostDisplay(s) {
  return s.labourCostDisplay || { invoices: { persons: true, organizations: false }, offers: { persons: false, organizations: false } };
}

export function labourCostDisplayHeadButton(ctx) {
  if (!ctx.can('accounting:write')) return '';
  return `<button class="cd-btn-primary" data-action="lc-save" ${ctx.s.lcSaving ? 'disabled' : ''}>${escapeHtml(ctx.s.lcSaving ? t('common.saving') : t('common.save'))}</button>`;
}

export function renderLabourCostDisplaySection(ctx) {
  const { s, can } = ctx;
  if (!can('accounting:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  const v = labourCostDisplay(s);
  const dis = s.lcSaving || !can('accounting:write') ? 'disabled' : '';
  const check = (id, checked, labelKey) => `
    <label class="cd-lh-check">
      <input type="checkbox" id="${id}" ${checked ? 'checked' : ''} ${dis}>
      ${escapeHtml(t(labelKey))}
    </label>`;

  return `
    <div class="cd-emp">
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('engineering', 18)}<h3>${escapeHtml(t('settings.labourCost.title'))}</h3></div>
        <p class="cd-sc-lead">${escapeHtml(t('settings.labourCost.lead'))}</p>
        <div class="cd-lc-cols">
          <div class="cd-lc-col">
            <div class="cd-lc-col-head">${escapeHtml(t('settings.labourCost.onInvoices'))}</div>
            ${check('lc-inv-persons', v.invoices.persons, 'settings.labourCost.forPersons')}
            ${check('lc-inv-orgs', v.invoices.organizations, 'settings.labourCost.forOrganizations')}
          </div>
          <div class="cd-lc-col">
            <div class="cd-lc-col-head">${escapeHtml(t('settings.labourCost.onOffers'))}</div>
            ${check('lc-off-persons', v.offers.persons, 'settings.labourCost.forPersons')}
            ${check('lc-off-orgs', v.offers.organizations, 'settings.labourCost.forOrganizations')}
          </div>
        </div>
      </div>
      <div class="cd-emp-info cd-sc-note">${icon('info', 18)}<span>${escapeHtml(t('settings.labourCost.note'))}</span></div>
    </div>`;
}

// ── Abschlagsrechnungen (Teil-/Akontorechnungen) ──────────
// plancraft hat hier genau einen Schalter: "Kumulative Abschläge als Standard
// festlegen". Kumulativ = auf dem PDF steht zusätzlich Gesamtbetrag Angebot /
// abzüglich bereits gestellter Abschläge / Rechnungsbetrag — reine Anzeige,
// der tatsächliche Rechnungsbetrag ist immer die Summe der gewählten Positionen.
function partialInvoiceSettings(s) {
  return s.partialInvoiceSettings || { cumulativeDefault: false };
}

export function partialInvoiceSettingsHeadButton(ctx) {
  if (!ctx.can('accounting:write')) return '';
  return `<button class="cd-btn-primary" data-action="pi-save" ${ctx.s.piSaving ? 'disabled' : ''}>${escapeHtml(ctx.s.piSaving ? t('common.saving') : t('common.save'))}</button>`;
}

export function renderPartialInvoiceSettingsSection(ctx) {
  const { s, can } = ctx;
  if (!can('accounting:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  const v = partialInvoiceSettings(s);
  const dis = s.piSaving || !can('accounting:write') ? 'disabled' : '';

  return `
    <div class="cd-emp">
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('request_quote', 18)}<h3>${escapeHtml(t('settings.partialInvoice.title'))}</h3></div>
        <label class="cd-lh-check">
          <input type="checkbox" id="pi-cumulativeDefault" ${v.cumulativeDefault ? 'checked' : ''} ${dis}>
          ${escapeHtml(t('settings.partialInvoice.cumulativeDefault'))}
        </label>
        <div class="cd-lh-hint">${escapeHtml(t('settings.partialInvoice.hint'))}</div>
      </div>
      <div class="cd-emp-info cd-sc-note">${icon('info', 18)}<span>${escapeHtml(t('settings.partialInvoice.note'))}</span></div>
    </div>`;
}
